import { FC, useEffect, useRef } from 'react';

import {
  FileTreeItemType,
  LoadMoreFunction,
  PaginatedFileTree,
  PortalNodeContextProvider,
} from '@dbt-labs/sourdough';

import { Spinner } from '@/components/Spinner';
import { useAccountNavigationContext } from '@/context/AccountNavigation/AccountNavigationProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';

interface AccountNavigationTreeProps {}

const ROOT_NODE_NAME = 'root';

export const AccountNavigationTree: FC<AccountNavigationTreeProps> = () => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const isWarehouseCatalogEnabled = hasAccountFeature('catalogIngestion');

  const { warehouses, dbtTree, favoriteTree } = useAccountNavigationContext();

  const {
    fileTree,
    generateChildrenForPath,
    selectedFile,
    selectedFolder,
    setSelectedFile,
    setSelectedFolder,
    openDirectories: openDirectoriesAccount,
    setOpenDirectories: setOpenDirectoriesAccount,
    onSort,
    initialized,
  } = warehouses.snowflake;

  const {
    fileTree: dbtFileTree,
    generateChildrenForPath: generateChildrenForPathDbt,
    selectedFile: dbtSelectedFile,
    selectedFolder: dbtSelectedFolder,
    setSelectedFile: setDbtSelectedFile,
    setSelectedFolder: setDbtSelectedFolder,
    initialized: dbtInitialized,
    openDirectories: openDirectoriesDbt,
    setOpenDirectories: setOpenDirectoriesDbt,
    onSort: onSortDbt,
  } = dbtTree;

  const {
    fileTree: favoriteFileTree,
    generateChildrenForPath: generateChildrenForPathFavorite,
    openDirectories: openDirectoriesFavorite,
    setOpenDirectories: setOpenDirectoriesFavorite,
    initialized: favoriteInitialized,
    onSort: onSortFavorite,
    setSelectedFile: setSelectedFileFavorite,
  } = favoriteTree;

  // should we do it this way or just initialize the fileTree with root + children
  // instead of calling generateChildrenForPath for each root folder?
  const hasRunDbtRef = useRef(false);
  const hasRunAccountRef = useRef(false);

  const loadRootFolders = (
    fileTree: FileTreeItemType[],
    rootNodeName: string,
    openDirectories: string[] | undefined,
    setOpenDirectories: any,
    loadChildren: LoadMoreFunction,
  ) => {
    const rootFolders = fileTree.filter((item) => item.parent === rootNodeName);
    const rootFolderPaths = rootFolders.map((item) => item.id);
    setOpenDirectories([...(openDirectories ?? []), ...rootFolderPaths]);
    rootFolderPaths.forEach(loadChildren);
  };

  useEffect(() => {
    if (hasRunAccountRef.current) return;
    if (warehouses.snowflake.isLoading) return;
    if (!fileTree?.length) return;
    if (openDirectoriesAccount.length > 0) return;

    loadRootFolders(
      fileTree,
      ROOT_NODE_NAME,
      openDirectoriesAccount,
      setOpenDirectoriesAccount,
      generateChildrenForPath,
    );

    hasRunAccountRef.current = true;
  }, [
    fileTree,
    generateChildrenForPath,
    openDirectoriesAccount,
    setOpenDirectoriesAccount,
    warehouses.snowflake.isLoading,
  ]);

  useEffect(() => {
    if (hasRunDbtRef.current) return;
    if (dbtTree.isLoading) return;
    if (!dbtFileTree?.length) return;
    if (openDirectoriesDbt.length > 0) return;
    loadRootFolders(
      dbtFileTree,
      ROOT_NODE_NAME,
      openDirectoriesDbt,
      setOpenDirectoriesDbt,
      generateChildrenForPathDbt,
    );

    hasRunDbtRef.current = true;
  }, [
    dbtFileTree,
    dbtTree.isLoading,
    generateChildrenForPathDbt,
    openDirectoriesDbt,
    setOpenDirectoriesDbt,
  ]);

  return (
    <PortalNodeContextProvider>
      <div className="flex-1 pb-24">
        <div className="space-y-1 overflow-y-hidden">
          {!favoriteInitialized && <Spinner />}
          {favoriteInitialized && favoriteFileTree.length > 1 && (
            <PaginatedFileTree
              items={favoriteFileTree}
              loadChildren={generateChildrenForPathFavorite}
              rootNodeName={ROOT_NODE_NAME}
              openDirectories={openDirectoriesFavorite}
              setOpenDirectories={setOpenDirectoriesFavorite}
              onSort={onSortFavorite}
              showExpandButtonOnHover
              isReadOnly
              maxHeight={Infinity}
              onFileSelect={setSelectedFileFavorite}
              itemFontSize={{
                root: 'text-lg',
                directory: 'text-base',
                file: 'text-base',
              }}
            />
          )}
        </div>
        <div className="space-y-1 overflow-y-hidden">
          {!dbtInitialized && <Spinner />}
          {dbtInitialized && (
            <PaginatedFileTree
              items={dbtFileTree}
              loadChildren={generateChildrenForPathDbt}
              selectedFile={dbtSelectedFile}
              onFileSelect={setDbtSelectedFile}
              onFolderSelect={setDbtSelectedFolder}
              openDirectories={openDirectoriesDbt}
              setOpenDirectories={setOpenDirectoriesDbt}
              rootNodeName={ROOT_NODE_NAME}
              enableCloseFolderOnSecondClick
              selectedFolder={dbtSelectedFolder}
              showExpandButtonOnHover
              isReadOnly
              maxHeight={Infinity}
              onSort={onSortDbt}
              itemFontSize={{
                root: 'text-lg',
                directory: 'text-base',
                file: 'text-base',
              }}
            />
          )}
        </div>
        {isWarehouseCatalogEnabled && (
          <div className="space-y-1 overflow-y-hidden">
            {!initialized && <Spinner />}
            {initialized && fileTree.length > 0 && (
              <PaginatedFileTree
                items={fileTree}
                loadChildren={generateChildrenForPath}
                selectedFile={selectedFile}
                onFileSelect={setSelectedFile}
                onFolderSelect={setSelectedFolder}
                openDirectories={openDirectoriesAccount}
                setOpenDirectories={setOpenDirectoriesAccount}
                rootNodeName={ROOT_NODE_NAME}
                enableCloseFolderOnSecondClick
                selectedFolder={selectedFolder}
                showExpandButtonOnHover
                isReadOnly
                maxHeight={Infinity}
                itemFontSize={{
                  root: 'text-lg',
                  directory: 'text-base',
                  file: 'text-base',
                }}
                onSort={onSort}
              />
            )}
          </div>
        )}
      </div>
    </PortalNodeContextProvider>
  );
};
